import React, { useEffect } from 'react';
import { Select } from '@sentara-health/optimahealth_web';
import { useTranslation } from 'react-i18next';
import { toSentancePascalCase, setEncryptedValue, UserDetails } from 'common';

type MemberRow = { mId: string; memberId: string; memberName?: string };

interface Props {
  members: MemberRow[] | undefined;
  memberId: string | null | undefined;
  setMemberId: (mId: string) => void;
  primaryUser?: 'P' | 'S';
  loginMID?: string;
  dataTestId?: string;
  disableWhenSingleMember?: boolean;
  isMemberIdCardDropdown?: boolean;
  customClassName?: string;
  onMemberSelect?: () => void;
}

const MemberSelectDropdown: React.FC<Props> = ({
  members = [],
  memberId,
  setMemberId,
  primaryUser,
  loginMID,
  dataTestId = 'memberSelectDropdown',
  disableWhenSingleMember = false,
  isMemberIdCardDropdown = false,
  customClassName = '',
  onMemberSelect,
}) => {
  const { t } = useTranslation();
  const hasSingleMember = (members?.length ?? 0) === 1;
  const only = hasSingleMember ? members[0] : undefined;

  useEffect(() => {
    if (only?.mId && only.mId !== memberId) {
      setMemberId(only.mId);
      setEncryptedValue(UserDetails.memberIdData, only.mId);
    }
  }, [only?.mId, memberId, setMemberId]);

  const labelFor = (m: MemberRow) => {
    const isPrimary = primaryUser === 'P' && loginMID === m.mId;
    const safeName =
      typeof m.memberName === 'string' && m.memberName.trim()
        ? toSentancePascalCase(m.memberName)
        : t('medicalClaims.unknownMember');
    let label = safeName;
    if (isPrimary) label += ` (${t('common.primary')})`;
    label += ` (${m.memberId})`;
    return label;
  };

  const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = e.target.value;
    if (selected !== memberId) {
      onMemberSelect?.();
    }
    setMemberId(selected);
    setEncryptedValue(UserDetails.memberIdData, selected);
  };

  return (
    <div className={`w-100 ${customClassName}`}>
      {disableWhenSingleMember && hasSingleMember ? (
        <div className={`form-control py-2`} data-testid={dataTestId} aria-readonly="true">
          {only ? labelFor(only) : t('common.selectOne')}
        </div>
      ) : (
        <Select
          id="member-dropdown"
          className="form-select"
          dataTestId={dataTestId}
          value={memberId ?? ''}
          onChange={onChange}
        >
          {!isMemberIdCardDropdown && !memberId && (
            <option value="" disabled hidden>
              {t('common.selectOne')}
            </option>
          )}
          {members.map((m, idx) => (
            <option key={m.mId} value={m.mId} data-testid={`memberOption${idx + 1}`}>
              {labelFor(m)}
            </option>
          ))}
        </Select>
      )}
    </div>
  );
};

export default MemberSelectDropdown;